import { useQuery } from 'react-query';
import { useMemo } from 'react';
import { ADVERT_API } from '../../../config';
import { getTemplateFromString } from '../../../utils/getTemplateFromString';
import { Field, FieldType, getFields, getSchema } from '../../dynamic-form';

interface Advert {
  name: string;
  partner: string;
  description: string;
  message: string;
  tasks: string[];
  expectations: string[];
  advantages: string[];
  offer: string[];
  image: {
    normal: string;
  }[];
  expiryDate: string;
  placeOfWork: string;
  language: string;
}

export const useAdvert = (advertId: string) => {
  const { data, isLoading, isError, error } = useQuery(
    ['advert', advertId],
    async () => {
      const response = await fetch(ADVERT_API.replace('{advertId}', advertId), {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        return Promise.reject(response.status);
      }

      return response.json();
    },
    { staleTime: 300000, retry: false },
  );

  return useMemo(() => {
    const { fields, submitButton, hasVisibleField, values } = getFields(
      Object.values<Field<FieldType>>(data?.data?.fields || {}),
    );

    const commonInfo = data?.data?.data_object?.common;
    const advertInfo: Advert = {
      name: commonInfo?.name,
      image: data?.data?.data_object?.portal_images ?? '',
      description: commonInfo?.description,
      message: (data?.data?.data_object?.forms?.message ?? '').replaceAll('\n', '<br/>'),
      partner: commonInfo?.partner,
      expiryDate: commonInfo?.expiry_date,
      tasks: commonInfo?.task,
      advantages: commonInfo?.advantages,
      expectations: commonInfo?.expectations,
      offer: commonInfo?.offer,
      placeOfWork: data?.data?.place_of_work,
      language: commonInfo?.advert_language?.toString().toLowerCase() || 'hu',
    };

    const schema = getSchema(fields);

    const template = getTemplateFromString(data?.data?.data_object?.forms?.template);

    return {
      isLoading,
      isError,
      error: error as number | null,
      fields,
      advert: advertInfo,
      initialValues: values,
      submit: submitButton,
      isValid: hasVisibleField,
      schema,
      template,
    };
  }, [isLoading, data, isError, error]);
};
